import React, { useState, useEffect } from 'react';
import { Bell, Send, Trash2, Search, AlertTriangle, CheckCircle2, X, Users, User } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const NotificationsManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Form states
  const [targetType, setTargetType] = useState('all'); // 'all' or 'specific'
  const [title, setTitle] = useState('');
  const [messageText, setMessageText] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [usersList, setUsersList] = useState([]);

  const [formLoading, setFormLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/notifications/for-message`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setNotifications(response.data.data || []);
      }
    } catch (err) {
      console.error("Error loading notifications:", err);
      setErrorMsg(err.response?.data?.message || err.message || "Error loading notifications.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUsers = async (val) => {
    setUserSearch(val);
    if (val.length < 2) return;
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/agencies/users/list?search=${val}`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setUsersList(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      // 1. Create a Message entity
      const msgRes = await axios.post(`${constant.API_BASE_URL}/messages`, {
        name: title,
        description: messageText,
        type: 'system',
        parent_type: 'bulk_alert',
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (!msgRes.data.success) {
        throw new Error(msgRes.data.message || "Failed to create message template");
      }

      const messageId = msgRes.data.data.id;

      // 2. Create the Notification referencing the message
      const payload = {
        name: title, // Backend validator needs this
        message_id: messageId,
        status: 'unread',
      };

      if (targetType === 'specific' && targetUserId) {
        payload.to_user_id = targetUserId;
      }

      const notifyRes = await axios.post(`${constant.API_BASE_URL}/notifications`, payload, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (notifyRes.data.success) {
        setSuccessMsg("System notification dispatched successfully!");
        setTitle('');
        setMessageText('');
        setTargetUserId('');
        setUserSearch('');
        setUsersList([]);
        fetchNotifications();
      } else {
        throw new Error(notifyRes.data.message || "Failed to dispatch notification");
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || "Something went wrong.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notification record?")) return;
    try {
      const res = await axios.delete(`${constant.API_BASE_URL}/notifications/${id}`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchNotifications();
      }
    } catch (err) {
      alert("Failed to delete notification record");
    }
  };

  const filteredNotifications = notifications.filter(n =>
    (n.message_title && n.message_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (n.message_description && n.message_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (n.to_user_name && n.to_user_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Notification System</h2>
          <p className="text-slate-500 mt-1">Broadcast system alerts, send direct messages, and manage system notifications.</p>
        </div>
        <button
          onClick={fetchNotifications}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          Refresh Notifications
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Side: Create Notification Form */}
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 h-fit">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-6">
            <Send size={20} className="text-pink-600" /> Dispatch System Alert
          </h3>

          {errorMsg && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm font-semibold flex items-center gap-2">
              <AlertTriangle size={18} /> 
              <span>
                {typeof errorMsg === 'object' ? (errorMsg.message || JSON.stringify(errorMsg)) : errorMsg}
              </span>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-4 bg-green-50 text-green-600 border border-green-100 rounded-xl text-sm font-semibold flex items-center gap-2">
              <CheckCircle2 size={18} /> {successMsg}
            </div>
          )}

          <form onSubmit={handleSendNotification} className="space-y-5">
            {/* Target Group Selector */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase">Target Audience</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="target"
                    checked={targetType === 'all'}
                    onChange={() => { setTargetType('all'); setTargetUserId(''); setUserSearch(''); }}
                  />
                  <span className="text-sm font-bold text-slate-700 flex items-center gap-1"><Users size={16} /> Broadcast (All Users)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="target"
                    checked={targetType === 'specific'}
                    onChange={() => setTargetType('specific')}
                  />
                  <span className="text-sm font-bold text-slate-700 flex items-center gap-1"><User size={16} /> Direct User</span>
                </label>
              </div>
            </div>

            {/* Recipient Lookup */}
            {targetType === 'specific' && (
              <div className="relative">
                <label className="text-xs font-bold text-slate-400 uppercase">Recipient Lookup</label>
                <input
                  required
                  placeholder="Type name or email to search..."
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={userSearch}
                  onChange={(e) => handleSearchUsers(e.target.value)}
                />
                {usersList.length > 0 && (
                  <div className="absolute top-20 left-0 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto">
                    {usersList.map((u) => (
                      <div
                        key={u.id}
                        onClick={() => {
                          setTargetUserId(u.id);
                          setUserSearch(`${u.first_name} ${u.last_name} (${u.email})`);
                          setUsersList([]);
                        }}
                        className="p-3 hover:bg-slate-50 cursor-pointer text-sm font-semibold border-b border-slate-100"
                      >
                        {u.first_name} {u.last_name} - {u.email}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notification Title */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase">Alert Title</label>
              <input
                required
                placeholder="e.g. Scheduled System Upgrade"
                className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Message Body */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase">Alert Body / Content</label>
              <textarea
                required
                placeholder="Write your alert message..."
                className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition h-28 resize-none"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={formLoading}
              className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl active:scale-95 transition shadow-lg shadow-pink-200"
            >
              {formLoading ? "Sending..." : "Dispatch Notification"}
            </button>
          </form>
        </div>

        {/* Right Side: Sent History List */}
        <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col h-[600px] overflow-hidden">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h3 className="text-lg font-bold text-slate-800">Dispatch Audit History</h3>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search history..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-pink-500/20"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="p-20 text-center flex-1">
              <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Loading audit trail...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-20 text-center text-slate-400 flex-1">
              <Bell size={48} className="mx-auto mb-4 opacity-30" />
              <p>No notifications recorded.</p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 pr-1 space-y-4">
              {filteredNotifications.map((n) => (
                <div key={n.notification_id} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl hover:shadow-md transition flex justify-between items-start gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-pink-100 text-pink-700 text-[10px] font-bold rounded-md uppercase">
                        {n.to_user_name ? "Direct" : "Broadcast"}
                      </span>
                      <h4 className="font-bold text-slate-800 text-sm">{n.message_title}</h4>
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2">{n.message_description}</p>
                    <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-4 gap-y-1 pt-1 font-semibold">
                      <span>Sender: {n.from_user_name || "Admin"}</span>
                      {n.to_user_name && <span>Recipient: {n.to_user_name}</span>}
                      <span>Date: {new Date(n.notification_created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteNotification(n.notification_id)}
                    className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-slate-100 transition shrink-0"
                    title="Delete Record"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsManagement;
